// `scriptctl view` — top-level command (§4). Renders a workspace's script.json
// into review.html at the workspace root. No model. For a video workspace it
// symlinks the episode media into <ws>/src/ep_NNN.mp4 so the review UI's relative
// video paths resolve. Fails clearly when the workspace has no script.json yet.

import * as fs from "node:fs";
import * as path from "node:path";

import { CliError, EXIT_INPUT, EXIT_OK, type CommandOutput } from "../../common.js";
import { renderReviewHtml } from "../../infra/ingest/review-renderer.js";
import { WorkspaceStore } from "../../infra/ingest/workspace-store.js";
import { runViewServer } from "./view-server.js";

type Dict = Record<string, unknown>;
const strOf = (v: unknown): string => (v === null || v === undefined ? "" : String(v));
const pad = (n: number): string => String(n).padStart(3, "0");

// Symlink each episode's media into <ws>/src/ep_NNN.mp4 (best-effort; the review
// UI seeks video by relative path). Only present for a video workspace.
function linkMedia(store: WorkspaceStore): void {
  if (!store.has("manifest.json")) return;
  const manifest = store.readJson<{ episodes?: Array<{ episode?: number; path?: string }> }>("manifest.json");
  if (!Array.isArray(manifest.episodes)) return;
  const srcDir = store.path("src");
  fs.mkdirSync(srcDir, { recursive: true });
  for (const e of manifest.episodes) {
    if (typeof e.episode !== "number" || typeof e.path !== "string") continue;
    const link = path.join(srcDir, `ep_${pad(e.episode)}.mp4`);
    try {
      fs.rmSync(link, { force: true });
      fs.symlinkSync(e.path, link);
    } catch {
      // best-effort: a missing source or unsupported fs just leaves the seek broken.
    }
  }
}

export async function commandView(opts: Dict): Promise<[CommandOutput, number]> {
  // --watch: live monitor server (progress + artifacts + context, auto-refresh),
  // runs until Ctrl-C. Works while ingest is still running (no script.json yet).
  // Local-only: it blocks on a web server + opens a browser, which would hang a
  // headless sandbox run — refuse there (the sandbox injects SANDBOX_PROJECT_GROUP_NO).
  if (opts["watch"]) {
    if ((process.env.SANDBOX_PROJECT_GROUP_NO ?? "").trim()) {
      throw new CliError("`view --watch` runs a local web server and opens a browser; it is local-only and unavailable in the sandbox.", {
              exitCode: EXIT_INPUT,
              errorCode: "VIEW_WATCH_SANDBOX",
              hint: "Use `scriptctl ingest status` for progress in the sandbox; run `view --watch` on your local machine.",
            });
    }
    return runViewServer(opts);
  }

  const workspacePath = strOf(opts["workspace_path"] || "workspace");
  const store = new WorkspaceStore(path.resolve(workspacePath));
  if (!store.has("script.json")) {
    throw new CliError("workspace has no script.json to render.", {
            exitCode: EXIT_INPUT,
            errorCode: "VIEW_NO_SCRIPT",
            hint: "Run `scriptctl ingest --source-path <src> --workspace-path <dir>` first.",
            data: { actual: [store.path("script.json")] },
          });
  }
  linkMedia(store);
  const html = renderReviewHtml(store.readJson("script.json"));
  store.writeText("review.html", html);
  const output = store.path("review.html");
  return [{ text: output, data: { output } }, EXIT_OK];
}
